import { yupResolver } from '@hookform/resolvers/yup'
import { Grid, Stack } from '@mui/material'
import deepEqual from 'fast-deep-equal'
import React, { useContext, useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useBoolean, usePrevious } from 'react-use'

import PageSection from 'components/common/PageSection'
import {
  AuthenticationFormValues,
  getAuthenticationFormValidationSchema,
  getDefaultAuthenticationFormValues
} from 'components/status-checks/authentication-form-utils'
import { ObservabilityToolAuthentication } from 'components/status-checks/form/authentication/ObservabilityToolAuthentication'
import { ObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import {
  ObservabilityToolType,
  ServiceStatusCheck
} from 'types/api/status-checks/status-check'

export const EditAuthenticationForm: React.FC<{
  authenticationName: string
  observabilityToolType: ObservabilityToolType
  domain?: string
  statusCheck?: ServiceStatusCheck
  handleCancel?: () => void
}> = ({ authenticationName, observabilityToolType, domain, handleCancel }) => {
  const [isAuthenticationSaving, setIsAuthenticationSaving] = useBoolean(false)

  const {
    getAvailableExternalIntegrations,
    hasErrorExternalIntegrations,
    isFetchingExternalIntegrations
  } = useStatusCheckExternalIntegrationsContext()

  const { isDrawerFlow } = useContext(ObservabilityToolAuthenticationContext)

  const externalIntegrations = getAvailableExternalIntegrations(false)

  const selectedExternalIntegration = externalIntegrations.find(
    (externalIntegration) => {
      const testWithoutDomain =
        externalIntegration.name === authenticationName &&
        externalIntegration.observabilityToolType === observabilityToolType

      if (domain) {
        return testWithoutDomain && externalIntegration.domain === domain
      }

      return testWithoutDomain
    }
  )

  const authenticationFormValidationSchema =
    getAuthenticationFormValidationSchema(externalIntegrations)

  const defaultValues = useMemo(
    () => getDefaultAuthenticationFormValues(selectedExternalIntegration),
    [selectedExternalIntegration]
  )
  const previousDefaultValues = usePrevious(defaultValues)

  const form = useForm<AuthenticationFormValues>({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(authenticationFormValidationSchema)
  })

  const { formState, reset } = form

  // Clears the form state when the external integrations have finished loading.
  // Make sure to compare any re-renders to previous values for deep comparison
  useEffect(() => {
    if (deepEqual(previousDefaultValues, defaultValues)) return

    reset(defaultValues)
  }, [defaultValues, previousDefaultValues, reset])

  return (
    <PageSection
      hasError={hasErrorExternalIntegrations}
      isLoading={isFetchingExternalIntegrations}
    >
      <FormProvider {...form}>
        <Grid
          container
          {...(isDrawerFlow && {
            sx: {
              mr: 6
            }
          })}
        >
          <Grid item lg={9} xs={12}>
            <Stack spacing={6}>
              <ObservabilityToolAuthentication
                externalIntegrations={externalIntegrations}
                handleCancel={handleCancel}
                isAuthenticationFormValid={formState.isValid}
                isAuthenticationSaving={isAuthenticationSaving}
                setIsAuthenticationSaving={setIsAuthenticationSaving}
              />
            </Stack>
          </Grid>
        </Grid>
      </FormProvider>
    </PageSection>
  )
}
